import { type ComponentType, useEffect } from "react";
import { ExternalLink, MonitorCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import List from "./components/List";
import { listConfiguration as listConfigurationAction } from "../../actions";
import { getCachedCountry, getTransifexLink, type TransifexLinkType } from "../../utils/transifex";
import { type Dispatch, type UnknownAction } from "redux";
import { WFPButton } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

export interface ConfigurationItem {
  count: number;
  description: string;
  frontend_endpoint: string;
  name: string;
  resource: string;
}

interface ConfigurationListData {
  count?: number;
  results?: ConfigurationItem[];
}

interface ConfigurationListState {
  data: ConfigurationListData;
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  listConfiguration: ConfigurationListState;
}

interface StateProps {
  configurationItems: ConfigurationItem[];
  errorMessage: string;
  isFetching: boolean;
}

interface DispatchProps {
  listConfiguration: () => void;
}

type ListProps = StateProps;

type WfpManagerConfigurationViewProps = StateProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const TRANSIFEX_MENU_ITEMS = [
  { type: "backend", labelKey: "configuration.transifex.backend" },
  { type: "mobile", labelKey: "configuration.transifex.mobile" },
  { type: "web", labelKey: "configuration.transifex.web" },
] as const satisfies readonly {
  labelKey: string;
  type: TransifexLinkType;
}[];

const ConfigurationList = List as ComponentType<ListProps>;

const TransifexMenu = () => {
  const { t } = useTranslation();
  const transifexMenuItems = TRANSIFEX_MENU_ITEMS.map(item => ({
    ...item,
    href: getTransifexLink(item.type),
  }));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <WFPButton type="button">
          <span>{t("configuration.transifex.title")}</span>
          <ExternalLink aria-hidden="true" className="size-4" />
        </WFPButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuGroup>
          {transifexMenuItems.map(item =>
            item.href ? (
              <DropdownMenuItem key={item.type} asChild>
                <a href={item.href} target="_blank" rel="noopener noreferrer">
                  {t(item.labelKey)}
                </a>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem key={item.type} disabled>
                {t(item.labelKey)}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const WfpManagerConfigurationView = ({
  isFetching,
  errorMessage,
  configurationItems,
  listConfiguration,
}: WfpManagerConfigurationViewProps) => {
  const { t } = useTranslation();
  const country = getCachedCountry();

  useEffect(() => {
    listConfiguration();
  }, [listConfiguration]);

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-y-1">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 bg-blue-100 text-blue-600">
              <MonitorCog />
            </span>
            <div className="flex flex-col gap-1">
              <h1 className="font-semibold text-2xl">
                {t("configuration.title", { country: country.name })}
              </h1>
            </div>
          </div>
          <p className="text-muted-foreground">{t("configuration.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <TransifexMenu />
        </div>
      </div>
      <ConfigurationList
        isFetching={isFetching}
        errorMessage={errorMessage}
        configurationItems={configurationItems}
      />
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  configurationItems: state.listConfiguration.data.results ?? [],
  isFetching: state.listConfiguration.isFetching,
  errorMessage: state.listConfiguration.errorMessage,
});

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listConfiguration: () => dispatchAction(dispatch, listConfigurationAction()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WfpManagerConfigurationView);
